import { keepPreviousData, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { reactQueryOptions } from "src/config/reactQuery.config";
import {
  getHistoricalPriceList,
  getHistoricalPriceListPackage,
  getProductDetailStock,
  getProductDetailStockPerWarehouse,
  getProductDetailStockWarehouse,
  getProductList,
  getProductListDetail,
  getProductListDetailStockAdjustmentHistory,
  getProductListDetailStockPurchaseHistory,
  getProductVariantList,
  getProductVariantListDetail,
} from "src/services/ProductList.service";
import {
  GetHistoricalPriceListRequest,
  GetHistoricalPriceResponse,
  ProductListDetailRequestBody,
  ProductListDetailStockAdjustmentHistoryParams,
  ProductListDetailStockParams,
  ProductListDetailStockPerWarehouseParams,
  ProductListDetailStockPurchaseHistoryParams,
  ProductListDetailStockWarehousesParams,
  ProductListParams,
  ProductVariantListDetailParams,
  ProductVariantListParams,
} from "./interfaces";
import { useGetAccessPermission } from "src/hooks/useGetAccessPermission";
import { AccessResource, ActionType } from "src/types/accessControl";

export const useGetProductList = (params: ProductListParams) => {
  const isAllowedProductList = useGetAccessPermission({
    resource: AccessResource["Product Management - Product List"],
    action: ActionType["Read"],
  });
  return useQuery({
    ...reactQueryOptions,
    queryKey: ["product-list", params],
    queryFn: () => getProductList(params),
    enabled: isAllowedProductList,
  });
};

export const useGetProductListDetail = (
  requestBody: ProductListDetailRequestBody
) => {
  return useQuery({
    ...reactQueryOptions,
    queryKey: ["product-list-detail", requestBody],
    queryFn: () => getProductListDetail(requestBody),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const useGetProductVariantList = (params: ProductVariantListParams) => {
  const isAllowedProductList = useGetAccessPermission({
    resource: AccessResource["Product Management - Product List"],
    action: ActionType["Read"],
  });
  return useQuery({
    ...reactQueryOptions,
    queryKey: ["product-variant-list", params],
    queryFn: () => getProductVariantList(params),
    enabled: isAllowedProductList,
  });
};

export const useGetProductVariantDetail = (
  params: ProductVariantListDetailParams
) => {
  return useQuery({
    ...reactQueryOptions,
    queryKey: ["product-variant-list", params],
    queryFn: () => getProductVariantListDetail(params),
  });
};

export const useGetProductDetailStock = (
  requestBody: ProductListDetailStockParams
) => {
  return useQuery({
    ...reactQueryOptions,
    queryKey: ["product-list-detail/product-stock", requestBody],
    queryFn: () => getProductDetailStock(requestBody),
  });
};
export const useGetProductDetailStockWarehouses = (
  requestBody: ProductListDetailStockWarehousesParams
) => {
  return useQuery({
    ...reactQueryOptions,
    queryKey: ["product-list-detail/product-stock", requestBody],
    queryFn: () => getProductDetailStockWarehouse(requestBody as any),
  });
};
export const useGetProductDetailStockPerWarehouse = (
  requestBody: ProductListDetailStockPerWarehouseParams
) => {
  return useQuery({
    ...reactQueryOptions,
    queryKey: ["product-list-detail/product-stock", requestBody],
    queryFn: () => getProductDetailStockPerWarehouse(requestBody),
  });
};

export const useGetProductStockAdjustmentHistory = (
  requestBody: ProductListDetailStockAdjustmentHistoryParams
) => {
  return useQuery({
    ...reactQueryOptions,
    queryKey: ["product-list-detail/stock-adjustment-history", requestBody],
    queryFn: () => getProductListDetailStockAdjustmentHistory(requestBody),
  });
};

export const useGetProductStockPurchaseHistory = (
  requestBody: ProductListDetailStockPurchaseHistoryParams
) => {
  return useQuery({
    ...reactQueryOptions,
    queryKey: ["product-list-detail/stock-purchase-history", requestBody],
    queryFn: () => getProductListDetailStockPurchaseHistory(requestBody),
  });
};

export const useGetHistoricalPriceList = (
  requestBody: GetHistoricalPriceListRequest,
  options?: UseQueryOptions<GetHistoricalPriceResponse>
) => {
  return useQuery({
    ...reactQueryOptions,
    ...options,
    queryKey: ["product-list-detail/historical-price-list", requestBody],
    queryFn: () => getHistoricalPriceList(requestBody),
  });
};

export const useGetHistoricalPriceListPackage = (
  requestBody: GetHistoricalPriceListRequest,
  options?: UseQueryOptions<GetHistoricalPriceResponse>
) => {
  return useQuery({
    ...reactQueryOptions,
    ...options,
    queryKey: ["product-list-detail/historical-price-list", requestBody],
    queryFn: () => getHistoricalPriceListPackage(requestBody),
  });
};
